import { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { PLATFORMS, platformLabel } from "../lib/platforms";
import { Pencil, Trash2, Plus, Sparkles, X, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";
import ImportDialog from "../components/ImportDialog";

const EMPTY = {
  title: "", description: "", price: 0, currency: "USD",
  image_url: "", affiliate_url: "", platform: "etsy", category: "",
  tags: [], featured: false,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const onSave = async (form) => {
    try {
      if (editing?.id) {
        await api.put(`/products/${editing.id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/products", form);
        toast.success("Product added");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-products">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="overline text-white/40">Inventory</p>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">Products.</h1>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="open-import"
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-white/5 transition-colors"
          >
            <Download className="w-4 h-4" /> Quick Import
          </button>
          <button
            data-testid="open-new-product"
            onClick={() => { setEditing(null); setOpen(true); }}
            className="inline-flex items-center gap-2 bg-[#E53935] text-white px-5 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#C62828] transition-colors"
          >
            <Plus className="w-4 h-4" /> New product
          </button>
        </div>
      </div>

      {importOpen && <ImportDialog onClose={() => setImportOpen(false)} onImported={load} />}

      <div className="admin-card">
        {loading ? <div className="p-8 text-white/50">Loading…</div> : products.length === 0 ? (
          <div className="p-12 text-center" data-testid="no-products">
            <p className="font-display font-black text-3xl tracking-tighter">No products yet.</p>
            <p className="text-white/50 text-sm mt-2">Add your first affiliate product to get started.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="products-table">
            <thead className="border-b border-white/10">
              <tr className="text-left text-xs uppercase tracking-wider text-white/40">
                <th className="p-4">Product</th>
                <th className="p-4">Platform</th>
                <th className="p-4">Category</th>
                <th className="p-4">Price</th>
                <th className="p-4">Clicks</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {products.map((p) => (
                <tr key={p.id} className="hover:bg-white/[0.02]">
                  <td className="p-4 flex items-center gap-3">
                    <img src={p.image_url} alt="" className="w-10 h-10 object-cover" />
                    <span className="font-medium">{p.title}</span>
                  </td>
                  <td className="p-4 text-white/70">{platformLabel(p.platform)}</td>
                  <td className="p-4 text-white/70">{p.category}</td>
                  <td className="p-4">${p.price.toFixed(2)}</td>
                  <td className="p-4 font-semibold text-[#E53935]">{p.clicks}</td>
                  <td className="p-4 flex gap-2">
                    <a href={p.affiliate_url} target="_blank" rel="noreferrer" className="p-2 hover:text-[#E53935]" title="Open affiliate URL">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button data-testid={`edit-${p.id}`} onClick={() => { setEditing(p); setOpen(true); }} className="p-2 hover:text-[#E53935]">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`delete-${p.id}`} onClick={() => onDelete(p.id)} className="p-2 hover:text-[#E53935]">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <ProductDialog
          initial={editing || EMPTY}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function ProductDialog({ initial, onClose, onSave }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial, tags: (initial.tags || []).join(", ") });
  const [aiLoading, setAiLoading] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: Number(form.price),
      tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
    };
    onSave(payload);
  };

  const generateAI = async () => {
    if (!form.title) { toast.error("Add a title first"); return; }
    setAiLoading(true);
    set("description", "");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/ai/describe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({
          title: form.title,
          platform: form.platform,
          category: form.category || "general",
          keywords: form.tags,
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const chunk = line.slice(6);
          if (chunk === "[DONE]") { setAiLoading(false); return; }
          acc += chunk;
          setForm(prev => ({ ...prev, description: acc }));
        }
      }
    } catch (e) {
      toast.error("AI generation failed");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="product-dialog">
      <div className="admin-card w-full max-w-2xl max-h-[90vh] overflow-auto admin-scroll">
        <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#121212]">
          <h2 className="font-display font-black text-2xl tracking-tighter">{initial.id ? "Edit product" : "New product"}</h2>
          <button onClick={onClose} data-testid="close-dialog" className="text-white/60 hover:text-white"><X /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <Field label="Title">
            <input data-testid="form-title" required value={form.title} onChange={(e) => set("title", e.target.value)} className="admin-input" />
          </Field>

          <div className="flex justify-between items-end gap-3">
            <p className="overline text-white/40">Description</p>
            <button type="button" data-testid="ai-generate" onClick={generateAI} disabled={aiLoading} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#E53935] hover:text-white transition-colors disabled:opacity-50">
              <Sparkles className="w-3.5 h-3.5" /> {aiLoading ? "Generating…" : "AI Write"}
            </button>
          </div>
          <textarea data-testid="form-description" required rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className="admin-input" />

          <div className="grid grid-cols-2 gap-4">
            <Field label="Price">
              <input data-testid="form-price" required type="number" step="0.01" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} className="admin-input" />
            </Field>
            <Field label="Currency">
              <input value={form.currency} onChange={(e) => set("currency", e.target.value)} className="admin-input" />
            </Field>
          </div>

          <Field label="Image URL">
            <input data-testid="form-image" required value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="admin-input" placeholder="https://..." />
          </Field>

          <Field label="Affiliate URL">
            <input data-testid="form-affiliate" required value={form.affiliate_url} onChange={(e) => set("affiliate_url", e.target.value)} className="admin-input" placeholder="https://etsy.com/..." />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Platform">
              <select data-testid="form-platform" value={form.platform} onChange={(e) => set("platform", e.target.value)} className="admin-input">
                {PLATFORMS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Category">
              <input data-testid="form-category" required value={form.category} onChange={(e) => set("category", e.target.value)} className="admin-input" placeholder="e.g. Home & Decor" />
            </Field>
          </div>

          <Field label="Tags (comma separated)">
            <input value={form.tags} onChange={(e) => set("tags", e.target.value)} className="admin-input" placeholder="handmade, organic" />
          </Field>

          <label className="flex items-center gap-2 text-sm">
            <input data-testid="form-featured" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} />
            Featured on storefront
          </label>

          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="px-5 py-3 border border-white/20 text-sm uppercase tracking-wider hover:bg-white/5">Cancel</button>
            <button type="submit" data-testid="form-submit" className="px-5 py-3 bg-[#E53935] text-white text-sm uppercase tracking-wider font-semibold hover:bg-[#C62828]">
              {initial.id ? "Save changes" : "Add product"}
            </button>
          </div>
        </form>
      </div>
      <style>{`.admin-input { width: 100%; background: #0A0A0A; border: 1px solid rgba(255,255,255,0.1); padding: 10px 12px; color: #fff; font-size: 14px; outline: none; } .admin-input:focus { border-color: #E53935; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline text-white/40 block mb-2">{label}</label>
      {children}
    </div>
  );
}
